import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { CookieService } from 'ngx-cookie';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { differenceInMinutes } from 'date-fns';
import { Router } from '@angular/router';
import { isNil, omitBy } from 'lodash';

interface Permission {
    cardio: boolean;
    nephro: boolean;
    all: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    public currentUser: Observable<any>;
    private companiesSubject: BehaviorSubject<any>;
    private currentUserSubject: BehaviorSubject<any>;
    private currentCompany = new BehaviorSubject<string | null>(null);
    private currentProfileSubject: BehaviorSubject<any>;
    public currentProfile: Observable<any>;

    public isAnonymizer: boolean;
    public isSuperAdmin: boolean;
    public isAdminMedical: boolean;
    public isAdminAccounting: boolean;

    public isUser: boolean;
    public isUserMedical: boolean;
    public isUserManagement: boolean;

    public isAdmin: boolean;
    public isMedical: boolean;
    public isManagement: boolean;

    public isEnableJfse: boolean;
    public isJfseReady = false;
    public enableSecured = false;
    public enableUnsync = false;
    public enableDegraded = false;

    public lastRequest: Date;

    public canBill: boolean;
    public canOnlyReadBills: boolean;
    public canInstallPatients: boolean;
    public canManageProfilesAndUsers: boolean;

    public canActivateView: Permission;
    public canEditAdministrativeInfos: Permission;
    public canEditMedicalInfos: Permission;
    public canEditInfos: Permission;
    public canIncludePatient: Permission;
    public canReincludePatient: Permission;
    public canManageMedicalAlerts: Permission;
    public canManageTechnicalAlerts: Permission;
    public canViewMedicalAlerts: Permission;
    public canSendSMS: Permission;
    public canConsultDocumentsAndPrescriptions: Permission;
    public canImportDocumentOnPatientRecord: Permission;
    public canAddTherapyCoachingSession: Permission;
    public canReportEvent: Permission;
    public canStopPatient: Permission;
    public canGeneratePrescription: Permission;
    public canGeneratePrescriptionBioTherapeutic: Permission;
    public canViewTherapyCoaching: Permission;
    public canViewTechnicalEventsAndComment: Permission;
    public canViewMedicalEventsAndComment: Permission;
    public canBeReferent: Permission;

    constructor(
        private cookieService: CookieService,
        private http: HttpClient,
        private router: Router,
    ) {
        const user: any = this.cookieService.getObject('currentUser');
        const companies: any =
            this.cookieService.getObject('companiesProfiles');
        const profile: any = this.cookieService.getObject('currentPermission');
        if (!user) {
            this.logout();
        } else {
            this.currentUserSubject = new BehaviorSubject<any>(user);
            this.currentUser = this.currentUserSubject.asObservable();
            this.currentProfileSubject = new BehaviorSubject(profile);
            this.currentProfile = this.currentProfileSubject.asObservable();
            this.currentCompany.next(profile?.companyId);
            this.companiesSubject = new BehaviorSubject(companies);
            this.updateRoleStates(user, profile);
        }
    }

    setCurrentProfile(profile) {
        const expires = new Date();
        expires.setTime(expires.getTime() + 8 * 60 * 60 * 1000);
        this.cookieService.putObject('currentPermission', profile, {
            domain: environment.cookieDomain,
            sameSite: 'lax',
            expires,
        });
        this.currentProfileSubject.next(profile);
        this.currentCompany.next(profile?.companyId);
        const user: any = this.cookieService.getObject('currentUser');
        this.updateRoleStates(user, profile);
        this.router.navigate(['dashboard/unknown']);
    }

    getCurrentCompany() {
        return this.currentCompany?.value;
    }

    getCurrentProfilId() {
        return this.currentProfileSubject?.value?._id;
    }

    getCurrentUser() {
        return this.currentUserSubject?.value;
    }

    getCurrentProfile() {
        return this.currentProfileSubject?.value;
    }

    getUserProfils() {
        const user = this.getCurrentUser();
        return [
            ...(this.companiesSubject?.value || []),
            ...(user.profilId ? [{ profilId: user.profilId }] : []),
        ];
    }

    setCurrentUser(user) {
        /**
         * We need to complete given user with existing user because we have some cases
         * where we'd lose user properties.
         * For instance, if user login with PSC, there is `user.isPSC` boolean property stored in
         * cookies (then in the subject) => but after updating his profile (i.e. default units),
         * the returned updated user (from backend) doesn't own the `isPSC` property.
         */

        const completeUser = {
            ...this.getCurrentUser(),
            ...this.getBasicUserForCookies(user),
        };
        this.currentUserSubject.next(completeUser);

        const expires = new Date();
        expires.setTime(expires.getTime() + 8 * 60 * 60 * 1000);
        this.cookieService.putObject('currentUser', completeUser, {
            domain: environment.cookieDomain,
            sameSite: 'lax',
            expires,
        });
        const profile = this.currentProfileSubject?.value;
        this.updateRoleStates(completeUser, profile);
    }

    getBasicUserForCookies(user) {
        const {
            _id,
            token,
            firstName,
            lastName,
            fullName,
            type,
            adminType,
            internalBillingExportEnabled,
            isPSC,
            prescriptionTemplateAuthorization,
            prescriptionTemplate,
            prescriptionTemplatesByCompany,
            isAnonymizer,
            language,
            biologicDataUnit,
            enableScale,
            enableTensio,
            jfse,
            isFilterRegionsEnabled,
        } = user;

        return omitBy(
            {
                _id,
                token,
                firstName,
                lastName,
                fullName,
                type,
                adminType,
                internalBillingExportEnabled,
                isPSC,
                isAnonymizer,
                prescriptionTemplateAuthorization,
                prescriptionTemplate,
                prescriptionTemplatesByCompany,
                language,
                biologicDataUnit,
                enableScale,
                enableTensio,
                jfse,
                isFilterRegionsEnabled,
            },
            isNil,
        );
    }

    logoutFromPSC(token: string): Observable<void> {
        return this.http.post<void>(`${environment.authApiUrl}/psc/logout`, {
            token,
        });
    }

    logout(expire = false) {
        const { token } = this.getCurrentUser() || {};

        this.logoutFromPSC(token).subscribe();

        let url = `${environment.authfrontURL}/?returnUrl=${window.location.href}`;

        if (expire) {
            url = `${url}&expire=true`;
        }

        this.cookieService.remove('currentUser');
        this.cookieService.remove('currentCompany');
        document.location.href = url;
    }

    private updateRoleAdmin(user) {
        this.isAdmin = user.type === 'admin';
        this.isAdminMedical = ['super', 'medical'].includes(user.adminType);
        this.isAdminAccounting = user.adminType === 'accounting';
        this.isSuperAdmin = user.adminType === 'super';
        this.isAnonymizer = user.isAnonymizer;
    }

    private updateRoleJfse(user) {
        this.isEnableJfse = user.jfse?.enableJfse;
        this.isJfseReady =
            !!user.jfse?.selectedIdJfse && !!user.jfse?.enableJfse;
        this.enableSecured = user.jfse?.enableSecured;
        this.enableUnsync = user.jfse?.enableUnsync;
        this.enableDegraded = user.jfse?.enableDegraded;
    }

    private updateRoleUser(user, profile) {
        this.isUserMedical = user.type === 'medical';
        this.isUserManagement = user.type === 'management';
        this.isUser = this.isUserMedical || this.isUserManagement;

        this.isMedical = this.isAdminMedical || this.isUserMedical;
        this.isManagement = this.isAdminAccounting || this.isUserManagement;
        this.canActivateView = this.generateDiseasePermissionsWithAdmin(
                profile,
                'canActivateView',
                true,
            );

        this.canBill = !!profile?.permissions?.general?.canBill;
        this.canInstallPatients =
            this.isAdmin ||
            !!profile?.permissions?.general?.canInstallPatients;
        this.canOnlyReadBills =
            this.isAdmin || !!profile?.permissions?.general?.canOnlyReadBills;
        this.canManageProfilesAndUsers =
            this.isAdmin ||
            !!profile?.permissions?.general?.canManageProfilesAndUsers;

        this.canEditMedicalInfos = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canEditMedicalInfos',
            true,
        );
        this.canViewTherapyCoaching = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canViewTherapyCoaching',
            true,
        );
        this.canManageMedicalAlerts = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canManageMedicalAlerts',
            true,
        );
        this.canManageTechnicalAlerts = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canManageTechnicalAlerts',
            true,
        );
        this.canViewMedicalAlerts = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canViewMedicalAlerts',
            true,
        );
        this.canViewMedicalEventsAndComment =
            this.generateDiseasePermissionsWithAdmin(
                profile,
                'canViewMedicalEventsAndComment',
                true,
            );
        this.canAddTherapyCoachingSession =
            this.generateDiseasePermissionsWithAdmin(
                profile,
                'canAddTherapyCoachingSession',
                true,
            );
        this.canIncludePatient = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canIncludePatient',
            true,
        );
        this.canReincludePatient = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canReincludePatient',
            true,
        );

        this.canEditAdministrativeInfos =
            this.generateDiseasePermissionsWithAdmin(
                profile,
                'canEditAdministrativeInfos',
            );
        this.canSendSMS = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canSendSMS',
        );
        this.canConsultDocumentsAndPrescriptions =
            this.generateDiseasePermissionsWithAdmin(
                profile,
                'canConsultDocumentsAndPrescriptions',
            );
        this.canImportDocumentOnPatientRecord =
            this.generateDiseasePermissionsWithAdmin(
                profile,
                'canImportDocumentOnPatientRecord',
            );
        this.canReportEvent = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canReportEvent',
        );
        this.canStopPatient = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canStopPatient',
        );
        this.canGeneratePrescription = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canGeneratePrescription',
        );

        this.canGeneratePrescriptionBioTherapeutic = this.generateDiseasePermissionsWithAdmin(
            profile,
            'canGeneratePrescriptionBioTherapeutic',
        );

        this.canViewTechnicalEventsAndComment =
            this.generateDiseasePermissionsWithAdmin(
                profile,
                'canViewTechnicalEventsAndComment',
            );

        this.canBeReferent = this.generateDiseasePermissions(
            profile,
            'canBeReferent',
        );

        this.canEditInfos = {
            cardio:
                this.canEditAdministrativeInfos.cardio ||
                this.canEditMedicalInfos.cardio,
            nephro:
                this.canEditAdministrativeInfos.nephro ||
                this.canEditMedicalInfos.nephro,
            all:
                this.canEditAdministrativeInfos.all ||
                this.canEditMedicalInfos.all,
        };
    }

    private generateDiseasePermissionsWithAdmin(
        profile,
        key,
        isMedical = false,
    ) {
        if (this.isAdmin) {
            const hasPermission = !isMedical || this.isAdminMedical;
            return {
                cardio: hasPermission,
                nephro: hasPermission,
                all: hasPermission,
            };
        }
        return this.generateDiseasePermissions(profile, key);
    }

    private generateDiseasePermissions(profile, key) {
        const cardio = profile?.permissions?.cardio?.[key];
        const nephro = profile?.permissions?.nephro?.[key];
        const all = cardio || nephro;
        return { cardio, nephro, all };
    }

    private updateRoleStates(user, profile) {
        this.updateRoleAdmin(user);
        this.updateRoleJfse(user);
        this.updateRoleUser(user, profile);
    }

    refreshToken() {
        const { token } = this.getCurrentUser() || {};
        return this.http
            .post<any>(`${environment.apiUrl}/sso/reauthenticate`, { token })
            .pipe(
                tap(({ user }) => {
                    this.setCurrentUser(user);
                }),
            );
    }

    setLastKeyDownTime() {
        if (differenceInMinutes(new Date(), this.lastRequest) >= 10) {
            this.lastRequest = new Date();
            this.refreshToken().subscribe();
        }
    }

    isFilterRegionsEnabled() {
        return !!this.getCurrentUser()?.isFilterRegionsEnabled;
    }

    isProfileWithPermission() {
        const profile = this.currentProfileSubject?.value;
        return profile?.permissions?.general?.canManageProfilesAndUsers;
    }
}
